import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiX, FiPlus, FiEdit2, FiBold, FiItalic, FiUnderline, FiList } from 'react-icons/fi';

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", 
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", 
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", 
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", 
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", 
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", 
  "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", 
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
];

function RichTextEditor({ value, onChange, placeholder }) {
  const editorRef = React.useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = (e) => {
    onChange(e.currentTarget.innerHTML);
  };

  const execCmd = (command, arg = null) => {
    document.execCommand(command, false, arg);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  return (
    <div className="custom-rich-editor">
      <div className="rich-editor-toolbar">
        <button type="button" onClick={() => execCmd('bold')} title="Bold" className="rich-tool-btn"><FiBold /></button>
        <button type="button" onClick={() => execCmd('italic')} title="Italic" className="rich-tool-btn"><FiItalic /></button>
        <button type="button" onClick={() => execCmd('underline')} title="Underline" className="rich-tool-btn"><FiUnderline /></button>
        <button type="button" onClick={() => execCmd('insertOrderedList')} title="Numeric List" className="rich-tool-btn" style={{ fontWeight: 'bold', fontSize: '12px' }}>1.</button>
        <button type="button" onClick={() => execCmd('insertUnorderedList')} title="Bullet List" className="rich-tool-btn"><FiList /></button>
        
        <select onChange={(e) => execCmd('fontName', e.target.value)} defaultValue="" className="rich-tool-select">
          <option value="" disabled>Font</option>
          <option value="Inter, sans-serif">Inter</option>
          <option value="Roboto, sans-serif">Roboto</option>
          <option value="Georgia, serif">Georgia</option>
          <option value="Courier New, monospace">Monospace</option>
        </select>

        <select onChange={(e) => execCmd('foreColor', e.target.value)} defaultValue="" className="rich-tool-select">
          <option value="" disabled>Color</option>
          <option value="#000000">Black</option>
          <option value="#334155">Dark Gray</option>
          <option value="#ef4444">Red</option>
          <option value="#3b82f6">Blue</option>
          <option value="#10b981">Green</option>
          <option value="#f59e0b">Orange</option>
          <option value="#8b5cf6">Purple</option>
        </select>
      </div>
      <div
        ref={editorRef}
        className="rich-editor-content"
        contentEditable
        onInput={handleInput}
        placeholder={placeholder}
      />
    </div>
  );
}

export default function ProductFormModal({
  isOpen,
  onClose,
  editingProduct,
  categories,
  onCategoryAdded,
  API_URL,
  token,
  triggerNotification,
  fetchData
}) {
  const defaultPackagingRow = () => ({ size_label: '500ml', price: '', compare_at_price: '', stock_quantity: '100', mue: '1' });
  
  const [productForm, setProductForm] = useState({
    title_first: '',
    title_last: '',
    subname_first: '',
    subname_last: '',
    sku: '',
    category_id: '1',
    short_description: '',
    description: '',
    active_ingredients: '',
    formulation: 'Concentrated Liquid',
    target_crops: '',
    dosage_application: '',
    key_benefits: '',
    shelf_life: '3 Years',
    packaging_available: '',
    images: [],
    brochure_image: null,
    packagingVariants: [defaultPackagingRow()],
    normal_delivery_time: '',
    fast_delivery_time: '',
    fast_delivery_addons: {}
  });

  // Additional UX States
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDesc, setNewCategoryDesc] = useState('');
  const [bulkAddonAmount, setBulkAddonAmount] = useState('');
  const [selectedStatesForBulk, setSelectedStatesForBulk] = useState([]);

  // Drag and drop handlers for images
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const reorderedImages = [...productForm.images];
    const [removed] = reorderedImages.splice(draggedIndex, 1);
    reorderedImages.splice(index, 0, removed);

    setProductForm(prev => ({
      ...prev,
      images: reorderedImages
    }));
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // State-wise addons bulk apply functions
  const handleSelectAllStates = () => {
    setSelectedStatesForBulk([...INDIAN_STATES]);
  };

  const handleClearStateSelection = () => {
    setSelectedStatesForBulk([]);
  };

  const handleToggleStateSelection = (state) => {
    setSelectedStatesForBulk(prev => 
      prev.includes(state) ? prev.filter(s => s !== state) : [...prev, state]
    );
  };

  const handleApplyBulkAddon = () => {
    if (bulkAddonAmount === '') return;
    const amount = parseFloat(bulkAddonAmount) || 0;
    setProductForm(prev => {
      const updatedAddons = { ...prev.fast_delivery_addons };
      selectedStatesForBulk.forEach(state => {
        updatedAddons[state] = amount;
      });
      return {
        ...prev,
        fast_delivery_addons: updatedAddons
      };
    });
    triggerNotification(`Applied ₹${amount} to ${selectedStatesForBulk.length} states.`);
  };

  // Re-sync form state when editingProduct changes
  useEffect(() => {
    if (isOpen) {
      if (editingProduct) {
        // Fetch full product details including variants and specifications
        const loadFullProduct = async () => {
          let fullProd = editingProduct;
          try {
            const slug = editingProduct.slug || editingProduct.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            const res = await axios.get(`${API_URL}/products/${slug}?t=${Date.now()}`);
            if (res.data.product) fullProd = res.data.product;
          } catch (err) {
            console.warn('Could not fetch full product details for edit:', err.message);
          }

          const findSpec = (label) => {
            const match = fullProd.specifications?.find(s => s.label === label || s.spec_label === label);
            return match ? (match.value || match.spec_value) : '';
          };

          const reconstructedVariants = (fullProd.variants && fullProd.variants.length > 0)
            ? fullProd.variants.map(v => ({
              size_label: v.size_label || v.sku?.split('-').pop() || '500ml',
              price: String(v.price || ''),
              compare_at_price: v.compare_at_price ? String(v.compare_at_price) : '',
              stock_quantity: v.stock_quantity !== undefined && v.stock_quantity !== null ? String(v.stock_quantity) : '100',
              mue: String(v.mue || '1')
            }))
            : [defaultPackagingRow()];

          setProductForm({
            title_first: fullProd.title_first || fullProd.title || '',
            title_last: fullProd.title_last || '',
            subname_first: fullProd.subname_first || '',
            subname_last: fullProd.subname_last || '',
            sku: fullProd.sku || '',
            category_id: String(fullProd.category_id || '1'),
            short_description: fullProd.short_description || '',
            description: fullProd.description || '',
            active_ingredients: findSpec('Active Ingredients'),
            formulation: findSpec('Formulation') || 'Concentrated Liquid',
            target_crops: findSpec('Target Crops'),
            dosage_application: findSpec('Dosage & Application'),
            key_benefits: findSpec('Key Benefits'),
            shelf_life: findSpec('Shelf Life') || '3 Years',
            packaging_available: findSpec('Packaging Available'),
            images: fullProd.images || [],
            brochure_image: fullProd.brochure_url || null,
            packagingVariants: reconstructedVariants,
            normal_delivery_time: fullProd.normal_delivery_time || '',
            fast_delivery_time: fullProd.fast_delivery_time || '',
            fast_delivery_addons: (() => {
              const addons = fullProd.fast_delivery_addons;
              if (!addons) return {};
              if (typeof addons === 'string') {
                try {
                  return JSON.parse(addons);
                } catch (e) {
                  return {};
                }
              }
              return addons;
            })()
          });
        };
        loadFullProduct();
      } else {
        const defaultCatId = categories.length > 0 ? String(categories[0].id) : '1';
        setProductForm({
          title_first: '',
          title_last: '',
          subname_first: '',
          subname_last: '',
          sku: '',
          category_id: defaultCatId,
          short_description: '',
          description: '',
          active_ingredients: '',
          formulation: 'Concentrated Liquid',
          target_crops: '',
          dosage_application: '',
          key_benefits: '',
          shelf_life: '3 Years',
          packaging_available: '',
          images: [],
          brochure_image: null,
          packagingVariants: [defaultPackagingRow()],
          normal_delivery_time: '',
          fast_delivery_time: '',
          fast_delivery_addons: {}
        });
      }
    }
  }, [isOpen, editingProduct, categories]);

  if (!isOpen) return null;

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (productForm.images.length + files.length > 5) {
      alert("You can upload a maximum of 5 images per product.");
      return;
    }
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size per file is 5MB.`);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setProductForm(prev => ({
          ...prev,
          images: [...prev.images, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleBrochureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('Brochure file is too large. Maximum size is 10MB.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setProductForm(prev => ({ ...prev, brochure_image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const addPackagingVariant = () => {
    setProductForm(prev => ({
      ...prev,
      packagingVariants: [...prev.packagingVariants, defaultPackagingRow()]
    }));
  };

  const removePackagingVariant = (idx) => {
    setProductForm(prev => ({
      ...prev,
      packagingVariants: prev.packagingVariants.filter((_, i) => i !== idx)
    }));
  };

  const updatePackagingVariant = (idx, field, value) => {
    setProductForm(prev => ({
      ...prev,
      packagingVariants: prev.packagingVariants.map((v, i) => i === idx ? { ...v, [field]: value } : v)
    }));
  };

  const removeImage = (indexToRemove) => {
    setProductForm(prev => ({
      ...prev,
      images: prev.images.filter((_, idx) => idx !== indexToRemove)
    }));
  };

  const handleAddCategoryClick = () => {
    setIsAddCategoryOpen(true);
  };

  const handleAddCategorySubmit = async (e) => {
    e.preventDefault();
    const cleanName = newCategoryName.trim();
    if (!cleanName) return;

    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      const res = await axios.post(`${API_URL}/categories`, { name: cleanName, description: newCategoryDesc.trim() }, { headers });
      if (res.data.category) {
        triggerNotification('New category added successfully.');
        onCategoryAdded(res.data.category);
        setProductForm(prev => ({
          ...prev,
          category_id: String(res.data.category.id)
        }));
        setNewCategoryName('');
        setNewCategoryDesc('');
        setIsAddCategoryOpen(false);
      }
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 'Failed to create category.';
      triggerNotification(errorMessage);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    if (!productForm.title_first || !productForm.sku) {
      triggerNotification('Product Name First Part and SKU are required.');
      return;
    }

    const validVariants = productForm.packagingVariants.filter(v => v.size_label && v.price && parseFloat(v.price) > 0);
    if (validVariants.length === 0) {
      triggerNotification('Please add at least one packaging variant with a size and price.');
      return;
    }

    const packagingAvailableText = productForm.packaging_available ||
      validVariants.map(v => v.size_label).join(', ');

    const payload = {
      category_id: parseInt(productForm.category_id),
      sku: productForm.sku,
      title_first: productForm.title_first,
      title_last: productForm.title_last,
      subname_first: productForm.subname_first,
      subname_last: productForm.subname_last,
      description: productForm.description,
      short_description: productForm.short_description,
      normal_delivery_time: productForm.normal_delivery_time || '',
      fast_delivery_time: productForm.fast_delivery_time || '',
      fast_delivery_addons: JSON.stringify(productForm.fast_delivery_addons || {}),
      specifications: [
        { label: 'Active Ingredients', value: productForm.active_ingredients },
        { label: 'Formulation', value: productForm.formulation },
        { label: 'Target Crops', value: productForm.target_crops },
        { label: 'Dosage & Application', value: productForm.dosage_application },
        { label: 'Key Benefits', value: productForm.key_benefits },
        { label: 'Shelf Life', value: productForm.shelf_life },
        { label: 'Packaging Available', value: packagingAvailableText }
      ],
      variants: validVariants.map(v => ({
        sku: `${productForm.sku}-${v.size_label.toUpperCase().replace(/[^A-Z0-9]/g, '')}`,
        size_label: v.size_label,
        price: parseFloat(v.price),
        compare_at_price: v.compare_at_price ? parseFloat(v.compare_at_price) : null,
        weight_grams: 0,
        stock_quantity: parseInt(v.stock_quantity) || 0,
        mue: parseInt(v.mue) || 1
      })),
      images: productForm.images,
      brochure_image: productForm.brochure_image || null
    };

    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/products/${editingProduct.id}`, payload, { headers });
      } else {
        await axios.post(`${API_URL}/products`, payload, { headers });
      }

      triggerNotification(editingProduct ? 'Product updated successfully.' : 'Product listing published successfully.');
      onClose();
      fetchData();
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 'Failed to process product submit.';
      triggerNotification(errorMessage);
    }
  };

  return (
    <div className="admin-modal-overlay-bg">
      <div className="admin-modal-form-card animate-scale-up" onClick={(e) => e.stopPropagation()}>
        <header className="modal-form-header">
          <h2>{editingProduct ? <><FiEdit2 className="modal-header-icon" /> Edit Product Details</> : <><FiPlus className="modal-header-icon" /> Publish New Product Listing</>}</h2>
          <button className="modal-close-x-btn" onClick={onClose} aria-label="Close modal"><FiX /></button>
        </header>
        <form onSubmit={handleProductSubmit} className="dashboard-grid-form">
          <div className="form-group flex-2">
            <label>Product Name (First Part - Blue) *</label>
            <input
              type="text"
              value={productForm.title_first}
              onChange={(e) => setProductForm({ ...productForm, title_first: e.target.value })}
              placeholder="e.g. Super"
              required
            />
          </div>

          <div className="form-group">
            <label>Product Name (Last Part - Yellow/Italic)</label>
            <input
              type="text"
              value={productForm.title_last}
              onChange={(e) => setProductForm({ ...productForm, title_last: e.target.value })}
              placeholder="e.g. Novel"
            />
          </div>

          <div className="form-group flex-2">
            <label>Product Subname (First Part - Blue)</label>
            <input
              type="text"
              value={productForm.subname_first}
              onChange={(e) => setProductForm({ ...productForm, subname_first: e.target.value })}
              placeholder="e.g. Bio-Stimulant"
            />
          </div>

          <div className="form-group">
            <label>Product Subname (Last Part - Yellow/Italic)</label>
            <input
              type="text"
              value={productForm.subname_last}
              onChange={(e) => setProductForm({ ...productForm, subname_last: e.target.value })}
              placeholder="e.g. Liquid"
            />
          </div>

          <div className="form-group">
            <label>Base SKU *</label>
            <input
              type="text"
              value={productForm.sku}
              onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
              placeholder="e.g. SN-200"
              required
            />
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Catalog Category *</label>
              <button
                type="button"
                className="link-action-btn"
                style={{ fontSize: '11px', color: '#10b981', fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer' }}
                onClick={handleAddCategoryClick}
              >
                + Add New
              </button>
            </div>
            <select
              value={productForm.category_id}
              onChange={(e) => setProductForm({ ...productForm, category_id: e.target.value })}
            >
              {categories.map(cat => (
                <option key={cat.id} value={String(cat.id)}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group flex-3">
            <label>Packaging Variants & Pricing *</label>
            <div className="packaging-variants-table">
              <div className="pkg-variants-header">
                <span>Size / Label</span>
                <span>Selling Price (₹)*</span>
                <span>Compare-At Price (₹)</span>
                <span>Stock Qty</span>
                <span>MUE</span>
                <span></span>
              </div>
              {productForm.packagingVariants.map((variant, idx) => (
                <div key={idx} className="pkg-variant-row">
                  <input
                    type="text"
                    value={variant.size_label}
                    onChange={(e) => updatePackagingVariant(idx, 'size_label', e.target.value)}
                    placeholder="e.g. 100ml"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={variant.price}
                    onChange={(e) => updatePackagingVariant(idx, 'price', e.target.value)}
                    placeholder="170.28"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={variant.compare_at_price}
                    onChange={(e) => updatePackagingVariant(idx, 'compare_at_price', e.target.value)}
                    placeholder="480.00"
                  />
                  <input
                    type="number"
                    value={variant.stock_quantity}
                    onChange={(e) => updatePackagingVariant(idx, 'stock_quantity', e.target.value)}
                    placeholder="100"
                  />
                  <input
                    type="number"
                    min="1"
                    value={variant.mue || '1'}
                    onChange={(e) => updatePackagingVariant(idx, 'mue', e.target.value)}
                    placeholder="1"
                  />
                  <button
                    type="button"
                    className="pkg-remove-btn"
                    onClick={() => removePackagingVariant(idx)}
                    title="Remove variant"
                    disabled={productForm.packagingVariants.length === 1}
                  >
                    <FiX />
                  </button>
                </div>
              ))}
              <button type="button" className="pkg-add-btn" onClick={addPackagingVariant}>
                <FiPlus /> Add Packaging Option
              </button>
            </div>
          </div>

          <div className="form-group flex-3">
            <label>Product Gallery Images (Maximum 5 images, Max 5MB each) - Drag to Rearrange</label>
            <div className="gallery-upload-container">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="gallery-file-input"
                id="product-gallery-input"
              />
              <label htmlFor="product-gallery-input" className="gallery-upload-trigger">
                <FiPlus className="upload-plus-icon" /> Choose Images
              </label>
            </div>
            {productForm.images && productForm.images.length > 0 && (
              <div className="gallery-previews-grid">
                {productForm.images.map((img, idx) => (
                  <div 
                    key={idx} 
                    className="gallery-preview-item"
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragEnd={handleDragEnd}
                    onDrop={(e) => handleDrop(e, idx)}
                    style={{ cursor: 'grab' }}
                  >
                    <img src={img} alt={`Preview ${idx + 1}`} className="gallery-preview-img" draggable={false} />
                    <button
                      type="button"
                      className="gallery-remove-btn"
                      onClick={() => removeImage(idx)}
                      title="Remove Image"
                    >
                      <FiX />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-group flex-3">
            <label>Product Brochure / A+ Content Image (Max 10MB)</label>
            <div className="gallery-upload-container">
              <input
                type="file"
                accept="image/*"
                onChange={handleBrochureUpload}
                className="gallery-file-input"
                id="product-brochure-input"
              />
              <label htmlFor="product-brochure-input" className="gallery-upload-trigger">
                <FiPlus className="upload-plus-icon" /> Upload Brochure / A+ Image
              </label>
            </div>
            {productForm.brochure_image && (
              <div className="brochure-preview-wrap">
                <img
                  src={productForm.brochure_image}
                  alt="Brochure Preview"
                  className="brochure-preview-img"
                />
                <button
                  type="button"
                  className="gallery-remove-btn brochure-remove-btn"
                  onClick={() => setProductForm(prev => ({ ...prev, brochure_image: null }))}
                  title="Remove Brochure"
                >
                  <FiX /> Remove
                </button>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Active Ingredients</label>
            <input
              type="text"
              value={productForm.active_ingredients}
              onChange={(e) => setProductForm({ ...productForm, active_ingredients: e.target.value })}
              placeholder="e.g. Humic Acid (12%), Fulvic Acid (5%)"
            />
          </div>

          <div className="form-group">
            <label>Formulation</label>
            <input
              type="text"
              value={productForm.formulation}
              onChange={(e) => setProductForm({ ...productForm, formulation: e.target.value })}
              placeholder="e.g. Concentrated Liquid"
            />
          </div>

          <div className="form-group">
            <label>Target Crops</label>
            <input
              type="text"
              value={productForm.target_crops}
              onChange={(e) => setProductForm({ ...productForm, target_crops: e.target.value })}
              placeholder="e.g. All Vegetables, Fruits, Grains"
            />
          </div>

          <div className="form-group">
            <label>Dosage & Application</label>
            <input
              type="text"
              value={productForm.dosage_application}
              onChange={(e) => setProductForm({ ...productForm, dosage_application: e.target.value })}
              placeholder="e.g. 2-3 ml per Liter of water"
            />
          </div>

          <div className="form-group">
            <label>Key Benefits</label>
            <input
              type="text"
              value={productForm.key_benefits}
              onChange={(e) => setProductForm({ ...productForm, key_benefits: e.target.value })}
              placeholder="e.g. Stimulates root growth, increases crop yield"
            />
          </div>

          <div className="form-group">
            <label>Shelf Life</label>
            <input
              type="text"
              value={productForm.shelf_life}
              onChange={(e) => setProductForm({ ...productForm, shelf_life: e.target.value })}
              placeholder="e.g. 3 Years"
            />
          </div>

          <div className="form-group">
            <label>Packaging Available</label>
            <input
              type="text"
              value={productForm.packaging_available}
              onChange={(e) => setProductForm({ ...productForm, packaging_available: e.target.value })}
              placeholder="e.g. 100ml, 250ml, 500ml, 1 Litre"
            />
          </div>

          <div className="form-group flex-3">
            <label>Short Description Overview *</label>
            <input
              type="text"
              value={productForm.short_description}
              onChange={(e) => setProductForm({ ...productForm, short_description: e.target.value })}
              placeholder="Brief 1-sentence descriptor summarizing plant growth benefits..."
              required
            />
          </div>

          <div className="form-group flex-3">
            <label>Detailed Specifications & Features Description</label>
            <RichTextEditor
              value={productForm.description}
              onChange={(val) => setProductForm({ ...productForm, description: val })}
              placeholder="Enter complete description of product chemical details, active ingredients, dosage usage rates and crop compatibilities..."
            />
          </div>

          <div className="form-group flex-2">
            <label>Normal Delivery Time</label>
            <input
              type="text"
              value={productForm.normal_delivery_time || ''}
              onChange={(e) => setProductForm({ ...productForm, normal_delivery_time: e.target.value })}
              placeholder="e.g. 5-7 business days"
            />
          </div>

          <div className="form-group">
            <label>Fast Delivery Time</label>
            <input
              type="text"
              value={productForm.fast_delivery_time || ''}
              onChange={(e) => setProductForm({ ...productForm, fast_delivery_time: e.target.value })}
              placeholder="e.g. 1-2 business days"
            />
          </div>

          <div className="form-group flex-3" style={{ marginTop: '10px' }}>
            <label style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b', textTransform: 'none', letterSpacing: 'normal' }}>
              Fast Delivery State-Wise Addons (₹)
            </label>
            
            {/* Bulk Apply Bar */}
            <div className="bulk-addon-apply-bar" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', background: '#f1f5f9', padding: '12px', borderRadius: '8px', marginBottom: '12px', marginTop: '6px', border: '1px dashed #cbd5e1' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>Bulk Apply Addon:</span>
              <input
                type="number"
                min="0"
                placeholder="Amount (₹)"
                value={bulkAddonAmount}
                onChange={(e) => setBulkAddonAmount(e.target.value)}
                style={{ width: '100px', padding: '6px 10px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#fff' }}
              />
              <button
                type="button"
                onClick={handleApplyBulkAddon}
                disabled={selectedStatesForBulk.length === 0 || bulkAddonAmount === ''}
                style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600', background: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: (selectedStatesForBulk.length === 0 || bulkAddonAmount === '') ? 0.5 : 1 }}
              >
                Apply to Selected ({selectedStatesForBulk.length})
              </button>
              <button
                type="button"
                onClick={handleSelectAllStates}
                style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600', background: '#e2e8f0', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleClearStateSelection}
                style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600', background: '#e2e8f0', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
              >
                Clear
              </button>
            </div>

            <div className="state-addons-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              {INDIAN_STATES.map(state => {
                const isChecked = selectedStatesForBulk.includes(state);
                return (
                  <div key={state} className="state-addon-item" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleStateSelection(state)}
                        style={{ cursor: 'pointer', width: '13px', height: '13px' }}
                      />
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={state}>
                        {state}
                      </span>
                    </div>
                    <input
                      type="number"
                      min="0"
                      value={productForm.fast_delivery_addons?.[state] || ''}
                      onChange={(e) => {
                        const val = e.target.value ? parseFloat(e.target.value) : '';
                        setProductForm(prev => ({
                          ...prev,
                          fast_delivery_addons: {
                            ...prev.fast_delivery_addons,
                            [state]: val
                          }
                        }));
                      }}
                      placeholder="Addon (₹)"
                      style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px', backgroundColor: '#ffffff' }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <footer className="form-action-footer flex-3">
            <button type="button" className="btn-secondary-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary-submit">
              {editingProduct ? 'Save Changes' : 'Publish Product'}
            </button>
          </footer>
        </form>
      </div>

      {isAddCategoryOpen && (
        <div className="admin-modal-overlay-bg second-modal-overlay" style={{ zIndex: 3000, position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="admin-modal-form-card animate-scale-up add-category-popup" style={{ maxWidth: '450px', padding: '24px', backgroundColor: '#fff', borderRadius: '16px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }} onClick={(e) => e.stopPropagation()}>
            <header className="modal-form-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Add New Category</h3>
              <button type="button" className="modal-close-x-btn" onClick={() => setIsAddCategoryOpen(false)} aria-label="Close modal" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '16px', color: '#64748b' }}><FiX /></button>
            </header>
            <form onSubmit={handleAddCategorySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', textTransform: 'none', letterSpacing: 'normal' }}>Category Name *</label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g. Bio-Fertilizers"
                  required
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', textTransform: 'none', letterSpacing: 'normal' }}>Description (Optional)</label>
                <textarea
                  rows="3"
                  value={newCategoryDesc}
                  onChange={(e) => setNewCategoryDesc(e.target.value)}
                  placeholder="Brief description of category..."
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                ></textarea>
              </div>
              <footer className="form-action-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                <button type="button" className="btn-secondary-cancel" onClick={() => setIsAddCategoryOpen(false)} style={{ padding: '10px 16px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', color: '#475569', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary-submit" style={{ padding: '10px 20px', border: 'none', borderRadius: '8px', background: '#10b981', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}>
                  Add Category
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
